/**
 * BillingHistory — /billing/history: credits balance, transaction table
 * (Data · Ação · Alvo · Créditos) and the credits store (3 Stripe packages).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Loader2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  lead_capture: { pt: "Captura de Lead", en: "Lead Capture" },
  enrichment: { pt: "Enriquecimento Claire", en: "Claire Enrichment" },
  victory_index: { pt: "Victory Index (IA)", en: "Victory Index (AI)" },
  topup: { pt: "Compra de créditos", en: "Credits purchase" },
  plan_renewal: { pt: "Renovação do plano", en: "Plan renewal" },
  welcome_gift: { pt: "Créditos de cortesia 🎁", en: "Courtesy credits 🎁" },
};

export default function BillingHistory() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState("");

  useEffect(() => {
    axios.get(`${API}/credits/history`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const buy = async (pkg) => {
    setBuying(pkg);
    try {
      const { data: res } = await axios.post(`${API}/credits/topup-checkout`, { package: pkg });
      if (res.url) window.location.href = res.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro no checkout"); }
    finally { setBuying(""); }
  };

  const target = (it) => it.meta?.lead || it.meta?.name || it.meta?.gov_lead || it.meta?.package || "—";

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="billing-history-page">
      <Sidebar />
      <main className="ml-[var(--al-sidebar-w,260px)] px-6 py-6 lg:px-10 max-w-[1100px]">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-[22px] font-extrabold inline-flex items-center gap-2">
            <Coins className="w-6 h-6 text-[#C9A24E]" />
            {isPt ? "Créditos & Histórico" : "Credits & History"}
          </h1>
          {data && (
            <div className="al-card px-4 py-2.5 flex items-center gap-2" data-testid="bh-balance">
              <Coins className="w-4 h-4 text-[#C9A24E]" />
              <span className="text-[18px] font-extrabold tabular-nums">{data.balance.toLocaleString()}</span>
              <span className="text-[11px] text-[var(--al-text-muted)]">{isPt ? "créditos disponíveis" : "credits available"}</span>
            </div>
          )}
        </header>

        {/* Credits store */}
        <section className="mb-8" data-testid="credits-store">
          <h2 className="text-base font-bold mb-3">{isPt ? "Loja de Créditos" : "Credits Store"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {data && Object.entries(data.packages).map(([id, p]) => (
              <div key={id}
                className={`al-card p-5 relative flex flex-col ${p.popular ? "ring-2 ring-[#C9A24E] shadow-lg" : ""}`}
                data-testid={`store-pack-${id}`}>
                {p.popular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9.5px] font-extrabold uppercase tracking-wide bg-[#C9A24E] text-[#1A1A2E] px-2.5 py-0.5 rounded-full">
                    {isPt ? "Mais Popular" : "Most Popular"}
                  </span>
                )}
                <div className="text-[13px] font-bold capitalize">{id}</div>
                <div className="text-[26px] font-extrabold mt-1 tabular-nums">{p.credits.toLocaleString()}
                  <span className="text-[12px] font-medium text-[var(--al-text-muted)]"> {isPt ? "créditos" : "credits"}</span>
                </div>
                <div className="text-[13px] text-[var(--al-text-muted)] mb-4">${p.usd}</div>
                <button type="button" disabled={!!buying} onClick={() => buy(id)}
                  data-testid={`store-buy-${id}`}
                  className={`mt-auto h-10 rounded-lg text-[12.5px] font-extrabold transition-colors disabled:opacity-60 ${
                    p.popular ? "bg-[#C9A24E] text-[#1A1A2E] hover:brightness-110"
                      : "border border-[#C9A24E] text-[#C9A24E] hover:bg-[#C9A24E]/10"}`}>
                  {buying === id ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (isPt ? "Comprar" : "Buy")}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Transactions table */}
        <section className="al-card overflow-hidden" data-testid="credits-table">
          <div className="px-5 py-3 border-b border-[var(--al-border)]">
            <h2 className="text-[13.5px] font-bold">{isPt ? "Histórico de transações" : "Transaction history"}</h2>
          </div>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] border-b border-[var(--al-border)]">
                <th className="px-5 py-2.5">{isPt ? "Data" : "Date"}</th>
                <th className="px-3 py-2.5">{isPt ? "Ação" : "Action"}</th>
                <th className="px-3 py-2.5">{isPt ? "Lead / Alvo" : "Lead / Target"}</th>
                <th className="px-5 py-2.5 text-right">{isPt ? "Créditos" : "Credits"}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((it) => {
                const gained = it.cost < 0;
                const lbl = ACTION_LABELS[it.action] || { pt: it.action, en: it.action };
                return (
                  <tr key={it.id} className="border-b border-[var(--al-border)] last:border-0 hover:bg-black/5">
                    <td className="px-5 py-2.5 text-[var(--al-text-muted)] tabular-nums whitespace-nowrap">
                      {(it.ts || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-3 py-2.5 font-medium">{isPt ? lbl.pt : lbl.en}
                      {!it.ok && <span className="ml-1.5 text-[9.5px] font-bold text-rose-500">{isPt ? "NEGADO" : "DENIED"}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-[var(--al-text-muted)] truncate max-w-[220px]">{target(it)}</td>
                    <td className={`px-5 py-2.5 text-right font-bold tabular-nums ${gained ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                      <span className="inline-flex items-center gap-1">
                        {gained ? <ArrowUpCircle className="w-3.5 h-3.5" /> : <ArrowDownCircle className="w-3.5 h-3.5" />}
                        {gained ? `+${-it.cost}` : `-${it.cost}`}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data && data.items.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-6 text-center text-[var(--al-text-muted)]">
                  {isPt ? "Nenhuma transação ainda" : "No transactions yet"}</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
